"use client"

import { useState, useCallback, useEffect, useRef } from "react"
import Link from "next/link"
import { Button } from "@/components/ui/button"
import { formatValue, isDualMeasurementUnit } from "@/lib/utils"
import { getSafeGameItemDescription } from "@/lib/game-item-description"
import { preloadImage } from "@/lib/preload-image"
import { GameItemImage } from "@/components/game/game-item-image"
import {
  ChevronUp,
  ChevronDown,
  Home,
  RotateCcw,
  X,
  Check,
  Copy,
  Sparkles,
  Loader2,
} from "lucide-react"
import { ShareButton } from "@/components/share/share-button"
import { trackProductEvent } from "@/lib/analytics/client"

interface GameItem {
  id: string
  name: string
  image: string | null
  value: number
  description: string | null
}

interface CustomGameClientProps {
  gameId: string
  title: string
  slug: string
  description: string | null
  valueLabel: string
  valueUnit: string | null
  valuePrefix: boolean
  items: GameItem[]
  plays: number
  creatorName: string
}

type GameState = "playing" | "correct" | "wrong" | "gameover"
const CORRECT_REVEAL_DURATION_MS = 1500
const FAIL_REVEAL_DURATION_MS = 3500

export function CustomGameClient({
  gameId,
  title,
  slug,
  valueLabel,
  valueUnit,
  valuePrefix,
  items: initialItems,
  plays,
  creatorName,
}: CustomGameClientProps) {
  const [items, setItems] = useState<GameItem[]>([])
  const [usedIds, setUsedIds] = useState<string[]>([])
  const [currentItem, setCurrentItem] = useState<GameItem | null>(null)
  const [nextItem, setNextItem] = useState<GameItem | null>(null)
  const [score, setScore] = useState(0)
  const [highScore, setHighScore] = useState(0)
  const [gameState, setGameState] = useState<GameState>("playing")
  const [revealedValue, setRevealedValue] = useState<number | null>(null)
  const [copied, setCopied] = useState(false)
  const gameStartedAtRef = useRef(Date.now())

  const shuffleArray = <T,>(array: T[]): T[] => {
    const shuffled = [...array]
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
    }
    return shuffled
  }

  const initializeGame = useCallback(() => {
    const shuffled = shuffleArray(initialItems)
    gameStartedAtRef.current = Date.now()
    setItems(shuffled)
    setCurrentItem(shuffled[0])
    setNextItem(shuffled[1])
    setUsedIds([shuffled[0].id, shuffled[1].id])
    preloadImage(shuffled[2]?.image, "high")
    preloadImage(shuffled[3]?.image)
    setScore(0)
    setGameState("playing")
    setRevealedValue(null)
  }, [initialItems])

  useEffect(() => {
    initializeGame()
  }, [initializeGame])

  useEffect(() => {
    trackProductEvent("custom_game_started", {
      customGameId: gameId,
      properties: {
        item_count: initialItems.length,
        prior_plays: plays,
      },
    })
  }, [gameId, initialItems.length, plays])

  const getNextUnusedItem = useCallback((): GameItem | null => {
    const available = items.filter((item) => !usedIds.includes(item.id))
    if (available.length === 0) {
      const newItems = items.filter(
        (item) => item.id !== currentItem?.id && item.id !== nextItem?.id
      )
      if (newItems.length === 0) return null
      setUsedIds([currentItem?.id || "", nextItem?.id || ""])
      return newItems[Math.floor(Math.random() * newItems.length)]
    }
    // The full deck is shuffled at the start. A predictable next item lets us
    // warm its image before the panel transition without reducing randomness.
    return available[0]
  }, [items, usedIds, currentItem, nextItem])

  const handleGuess = (guess: "higher" | "lower") => {
    if (!currentItem || !nextItem || gameState !== "playing") return

    const isCorrect =
      guess === "higher"
        ? nextItem.value >= currentItem.value
        : nextItem.value <= currentItem.value

    setRevealedValue(nextItem.value)
    trackProductEvent("custom_game_answered", {
      customGameId: gameId,
      properties: {
        guess,
        correct: isCorrect,
        round: score + 1,
        score_after: isCorrect ? score + 1 : score,
      },
    })

    if (isCorrect) {
      setGameState("correct")
      setScore((prev) => prev + 1)

      const newNextItem = getNextUnusedItem()
      const lookaheadItem = items.find(
        (item) =>
          !usedIds.includes(item.id) &&
          item.id !== newNextItem?.id
      )
      preloadImage(lookaheadItem?.image, "high")

      setTimeout(() => {
        if (newNextItem) {
          setUsedIds((prev) => [...prev, newNextItem.id])
        }
        setCurrentItem(nextItem)
        setNextItem(newNextItem)
        setRevealedValue(null)
        setGameState("playing")
      }, CORRECT_REVEAL_DURATION_MS)
    } else {
      setGameState("wrong")
      setTimeout(() => {
        setGameState("gameover")
        trackProductEvent("custom_game_completed", {
          customGameId: gameId,
          properties: {
            score,
            rounds_answered: score + 1,
            duration_seconds: Math.max(
              0,
              Math.round((Date.now() - gameStartedAtRef.current) / 1000)
            ),
          },
        })
        if (score + 1 > highScore) {
          setHighScore(score)
        }
      }, FAIL_REVEAL_DURATION_MS)
    }
  }

  const displayValue = (value: number) => {
    return formatValue(value, valueUnit || undefined, valuePrefix)
  }
  const valueTextSize = isDualMeasurementUnit(valueUnit)
    ? "text-2xl sm:text-3xl md:text-4xl"
    : "text-4xl md:text-5xl"

  const handleCopyLink = () => {
    navigator.clipboard.writeText(`https://playhigherlower.com/custom/${slug}`)
    trackProductEvent("custom_game_shared", { customGameId: gameId })
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  if (!currentItem || !nextItem) {
    return (
      <div className="studio-game-stage min-h-screen flex items-center justify-center bg-zinc-950">
        <Loader2 className="w-8 h-8 text-[#b83f34] animate-spin" />
      </div>
    )
  }

  const currentDescription = getSafeGameItemDescription({
    description: currentItem.description,
    value: currentItem.value,
    valueLabel,
    valueUnit,
    valuePrefix,
  })
  const nextDescription = getSafeGameItemDescription({
    description: nextItem.description,
    value: nextItem.value,
    valueLabel,
    valueUnit,
    valuePrefix,
  })

  return (
    <div className="studio-game-stage min-h-screen flex flex-col bg-zinc-950">
      <h1 className="sr-only">{title} - Custom Higher Lower Game</h1>

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 p-4 bg-zinc-950/80 backdrop-blur-xl">
        <div className="container mx-auto flex items-center justify-between">
          <Link href="/custom">
            <Button variant="ghost" size="sm" className="text-white/70 hover:text-white hover:bg-white/10 rounded-full gap-2">
              <Home className="h-4 w-4" />
              <span className="hidden sm:inline">Exit</span>
            </Button>
          </Link>

          <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10">
            <Sparkles className="w-4 h-4 text-[#b83f34]" />
            <span className="text-white/60 text-sm truncate max-w-[150px] sm:max-w-none">{title}</span>
          </div>

          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="text-3xl font-black text-white">{score}</div>
              <div className="text-xs text-white/40 uppercase tracking-wide">Score</div>
            </div>
            {highScore > 0 && (
              <div className="text-right pl-4 border-l border-white/20">
                <div className="text-xl font-bold text-white/60">{highScore}</div>
                <div className="text-xs text-white/40 uppercase tracking-wide">Best</div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Game Area */}
      <div className="flex-1 flex flex-col md:flex-row pt-20">
        {/* Current Item */}
        <div
          className={`flex-1 relative flex items-center justify-center p-6 md:p-12 overflow-hidden ${
            !currentItem.image ? "bg-gradient-to-br from-orange-900/80 to-rose-950/90" : ""
          }`}
          style={{ backgroundColor: "#431b16" }}
        >
          <GameItemImage
            src={currentItem.image}
            itemName={currentItem.name}
            gameTypeName={title}
            fallbackClassName="bg-gradient-to-br from-orange-900/80 to-rose-950/90"
            priority
          />
          <div className="text-center text-white max-w-lg relative z-10">
            <div className="text-sm uppercase tracking-widest text-white/50 mb-4">Current</div>
            <h2 className="text-2xl md:text-4xl font-bold mb-3">{currentItem.name}</h2>
            {currentDescription && (
              <p className="text-white/60 mb-6 text-sm md:text-base">{currentDescription}</p>
            )}
            <div className="inline-block px-8 py-4 rounded-2xl bg-white/10 backdrop-blur-xl border border-white/20">
              <div className="text-sm text-white/60 mb-1">{valueLabel}</div>
              <div className={`${valueTextSize} font-black`}>{displayValue(currentItem.value)}</div>
            </div>
          </div>
        </div>

        {/* VS Divider */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20 hidden md:flex">
          <div className={`w-20 h-20 rounded-full flex items-center justify-center shadow-2xl transition-all duration-300 ${
            gameState === "correct" ? "bg-emerald-500 scale-110"
              : gameState === "wrong" ? "bg-rose-500 scale-110"
              : "bg-white"
          }`}>
            {gameState === "correct" ? (
              <Check className="h-10 w-10 text-white" />
            ) : gameState === "wrong" ? (
              <X className="h-10 w-10 text-white" />
            ) : (
              <span className="text-2xl font-black text-zinc-900">VS</span>
            )}
          </div>
        </div>

        <div className="md:hidden flex justify-center -my-6 z-20">
          <div className={`w-14 h-14 rounded-full flex items-center justify-center shadow-xl transition-all duration-300 ${
            gameState === "correct" ? "bg-emerald-500"
              : gameState === "wrong" ? "bg-rose-500"
              : "bg-white"
          }`}>
            {gameState === "correct" ? (
              <Check className="h-7 w-7 text-white" />
            ) : gameState === "wrong" ? (
              <X className="h-7 w-7 text-white" />
            ) : (
              <span className="text-lg font-black text-zinc-900">VS</span>
            )}
          </div>
        </div>

        {/* Next Item */}
        <div
          className={`flex-1 relative flex items-center justify-center p-6 md:p-12 overflow-hidden ${
            !nextItem.image ? "bg-gradient-to-tl from-orange-900/80 to-rose-950/90" : ""
          }`}
          style={{ backgroundColor: "#431b16" }}
        >
          <GameItemImage
            src={nextItem.image}
            itemName={nextItem.name}
            gameTypeName={title}
            fallbackClassName="bg-gradient-to-tl from-orange-900/80 to-rose-950/90"
            priority
          />
          <div className="text-center text-white max-w-lg relative z-10">
            <div className="text-sm uppercase tracking-widest text-white/50 mb-4">Guess</div>
            <h2 className="text-2xl md:text-4xl font-bold mb-3">{nextItem.name}</h2>
            {nextDescription && (
              <p className="text-white/60 mb-6 text-sm md:text-base">{nextDescription}</p>
            )}

            {revealedValue !== null ? (
              <div className={`inline-block px-8 py-4 rounded-2xl backdrop-blur-xl border transition-all duration-500 ${
                gameState === "correct"
                  ? "bg-emerald-500/20 border-emerald-500/50"
                  : gameState === "wrong"
                    ? "bg-rose-500/20 border-rose-500/50"
                    : "bg-white/10 border-white/20"
              }`}>
                <div className="text-sm text-white/60 mb-1">{valueLabel}</div>
                <div className={`${valueTextSize} font-black ${
                  gameState === "correct" ? "text-emerald-400" : gameState === "wrong" ? "text-rose-400" : "text-white"
                }`}>
                  {displayValue(revealedValue)}
                </div>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={() => handleGuess("higher")}
                  disabled={gameState !== "playing"}
                  className="h-14 px-8 text-lg font-bold bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl shadow-lg shadow-emerald-500/25 gap-2"
                >
                  <ChevronUp className="h-6 w-6" />
                  Higher
                </Button>
                <Button
                  onClick={() => handleGuess("lower")}
                  disabled={gameState !== "playing"}
                  className="h-14 px-8 text-lg font-bold bg-rose-500 hover:bg-rose-600 text-white rounded-xl shadow-lg shadow-rose-500/25 gap-2"
                >
                  <ChevronDown className="h-6 w-6" />
                  Lower
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Game Over Modal */}
      {gameState === "gameover" && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-xl flex items-center justify-center z-50 p-4">
          <div className="studio-game-card bg-zinc-900 rounded-3xl p-8 md:p-12 max-w-md w-full text-center border border-zinc-800">
            <div className="w-20 h-20 rounded-full bg-[#ff6f61]/20 flex items-center justify-center mx-auto mb-8">
              <Sparkles className="h-10 w-10 text-[#b83f34]" />
            </div>

            <h2 className="text-3xl font-bold text-white mb-3">Game Over!</h2>
            <p className="text-zinc-400 mb-2">{title}</p>
            <p className="text-zinc-500 text-sm mb-6">by {creatorName} &middot; {plays.toLocaleString()} plays</p>

            <div className="mb-10">
              <div className="text-5xl font-black text-white">{score}</div>
              <div className="text-sm text-zinc-500 uppercase tracking-wide">Final Score</div>
            </div>

            <div className="flex flex-col gap-3">
              <ShareButton
                type="standard"
                score={score}
                gameType={title}
              />
              <Button
                onClick={handleCopyLink}
                className="coral-cta h-14 text-lg rounded-xl gap-2 border bg-[#ff6f61] text-[#171717]"
              >
                {copied ? <Check className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
                {copied ? "Copied!" : "Copy Game Link"}
              </Button>
              <Button
                onClick={initializeGame}
                className="h-14 text-lg rounded-xl gap-2 border border-[#171717]/15 bg-[#eee6db] text-[#171717] hover:bg-[#e5dccf]"
              >
                <RotateCcw className="h-5 w-5" />
                Play Again
              </Button>
              <Link href="/custom" className="w-full">
                <Button variant="outline" className="w-full h-14 text-lg rounded-xl border-[#171717]/20 text-[#171717] hover:bg-[#eee6db]">
                  Browse More Games
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
